import { Injectable } from '@angular/core';
import { ViewportService } from './viewport.service';

export type DefaultLandingPage = 'landing' | 'kiln-list' | 'information-radiator';

@Injectable({
  providedIn: 'root',
})
export class DefaultLandingService {
  static readonly STORAGE_KEY = 'defaultLandingPage';
  static readonly OPTIONS: ReadonlyArray<{
    value: DefaultLandingPage;
    labelKey: string;
  }> = [
    { value: 'landing', labelKey: 'USER_SETTINGS.DEFAULT_LANDING_QUICK_ACTIONS' },
    { value: 'kiln-list', labelKey: 'USER_SETTINGS.DEFAULT_LANDING_KILN_LIST' },
    { value: 'information-radiator', labelKey: 'USER_SETTINGS.DEFAULT_LANDING_DASHBOARD' },
  ];

  constructor(private viewport: ViewportService) {}

  isValid(value: unknown): value is DefaultLandingPage {
    return value === 'landing' || value === 'kiln-list' || value === 'information-radiator';
  }

  /** Explicit user choice, or null if they have not set one. */
  getPreference(): DefaultLandingPage | null {
    try {
      const stored = localStorage.getItem(DefaultLandingService.STORAGE_KEY);
      if (this.isValid(stored)) {
        return stored;
      }
    } catch {
      // Ignore storage errors (private mode, etc.).
    }
    return null;
  }

  setPreference(page: DefaultLandingPage): void {
    if (!this.isValid(page)) {
      return;
    }
    try {
      localStorage.setItem(DefaultLandingService.STORAGE_KEY, page);
    } catch {
      // Ignore storage errors (private mode, etc.).
    }
  }

  /** Default when the user has not chosen a home screen for the current viewport. */
  getViewportDefaultPreference(): DefaultLandingPage {
    return this.viewport.usesLargeScreenDefaultLanding() ? 'kiln-list' : 'landing';
  }

  /**
   * Route used after sign-in / Home: stored preference when valid for the
   * viewport, otherwise Quick Actions on small screens and Kiln List on large.
   * Quick Actions never wins on large screens — Kiln List or Dashboard does.
   */
  getHomePath(): DefaultLandingPage {
    const stored = this.getPreference();

    if (this.viewport.usesLargeScreenDefaultLanding()) {
      if (stored === 'information-radiator') {
        return 'information-radiator';
      }
      return 'kiln-list';
    }

    if (stored) {
      return stored;
    }
    return 'landing';
  }
}
